// src/App.jsx
import React, { useEffect, useState, lazy, Suspense } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { auth } from "./firebase";
import { db } from "./firebase";
import { AuthContext } from "./context/AuthContext";
import { FinancialYearProvider } from "./context/FinancialYearContext";
import {
  RouteLoadingListener,
  LazyRouteFallback,
  FullPageLoader,
} from "./loading";
// Components
import Login from "./components/Login";
import ProtectedRoute from "./components/ProtectedRoute";
import ProfitLossProfessional from "./components/ProfitLossProfessional";
import CashBook from "./components/CashBook";
import BankBook from "./components/BankBook";
import ExcelImport from "./components/ExcelImport";
import PendingOrderEmailSettingsPage from "./pages/PendingOrderEmailSettingsPage";
import BillPrintPage from "./screens/BillPrintPage";

// Pages
import Dashboard from "./pages/Dashboard";
import BillingScreen from "./pages/BillingScreen";
import VoucherTransactionPage from "./pages/VoucherTransactionPage";
import { DEFAULT_INWARD_PAGE, voucherPagePermissions } from "./config/voucherTransactionPages";
import Reports from "./pages/Reports";
import PartyCreation from "./pages/PartyCreation";
import AdminPanel from "./pages/AdminPanel";
import AdminDashboard from "./pages/AdminDashboard";
import FirestoreDataManagerPage from "./pages/admin/FirestoreDataManagerPage";
import MonthlyProductReport from "./pages/MonthlyProductReport";
import AddProductPage from "./pages/AddProductPage";
import StockReport from "./pages/StockReport";
import MovementAnalysis from "./pages/MovementAnalysis";
import StockForecastPage from "./pages/StockForecastPage";
import PartyAgeingReport from "./pages/PartyAgeingReport";
import PartyLedgerPage from "./pages/PartyLedgerPage";
import PartyOrderHistoryPage from "./pages/PartyOrderHistoryPage";
import GroupMasterPage from "./pages/GroupMasterPage";
import GroupCommissionSettingsPage from "./pages/GroupCommissionSettingsPage";
import BrandMasterPage from "./pages/BrandMasterPage";
import UnitMasterPage from "./pages/UnitMasterPage";
import GstMasterPage from "./pages/GstMasterPage";
import HsnMasterPage from "./pages/HsnMasterPage";
import CreditDueMasterPage from "./pages/CreditDueMasterPage";
import Gstr1Page from "./pages/Gstr1Page";
import PurchaseOrdersList from "./pages/PurchaseOrdersList";
import PendingOrdersPage from "./pages/PendingOrdersPage";
import PayrollPage from "./pages/PayrollPage";
import EmployeesPage from "./pages/EmployeesPage";
import HolidaysPage from "./pages/HolidaysPage";
import DailyHoursPage from "./pages/DailyHoursPage";
import DailyLateReportPage from "./pages/DailyLateReportPage";
import PayrollReportsHub from "./pages/payroll/PayrollReportsHub";
import PayrollEmployeeDirectoryReport from "./pages/payroll/PayrollEmployeeDirectoryReport";
import PayrollTenureMobilityReport from "./pages/payroll/PayrollTenureMobilityReport";
import PayrollPeriodAttendanceReport from "./pages/payroll/PayrollPeriodAttendanceReport";
import PayrollEmployeeAttendanceCard from "./pages/payroll/PayrollEmployeeAttendanceCard";
import PayrollOtSummaryReport from "./pages/payroll/PayrollOtSummaryReport";
import PayrollShiftAdherenceReport from "./pages/payroll/PayrollShiftAdherenceReport";
import PayrollBranchComparisonReport from "./pages/payroll/PayrollBranchComparisonReport";
import PayrollAttendanceAuditNote from "./pages/payroll/PayrollAttendanceAuditNote";
import PayrollYearlyAttendancePage from "./pages/payroll/PayrollYearlyAttendancePage";
import PayrollAttendanceImagesPage from "./pages/payroll/PayrollAttendanceImagesPage";
import PayrollMonthlyLeaveReport from "./pages/payroll/PayrollMonthlyLeaveReport";
import OrderReportsPage from "./pages/OrderReportsPage";
import CustomerEntryPage from "./pages/CustomerEntryPage";
import AnnouncementBanner from "./components/AnnouncementBanner";
import MaintenanceGate from "./components/MaintenanceGate";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import PersonalDataPage from "./pages/PersonalDataPage";
import PartyDiscounts from "./pages/PartyDiscounts";
import PartyDiscountAmountsPage from "./pages/PartyDiscountAmountsPage";
import MonthlySaleReportImportPage from "./pages/MonthlySaleReportImportPage";
import MonthlySaleReportImportPrecalcPage from "./pages/MonthlySaleReportImportPrecalcPage";
import ExpenseUploadPage from "./pages/ExpenseUploadPage";
import ExpensePartyMonthlyReportPage from "./pages/ExpensePartyMonthlyReportPage";
import PartyBrandMonthlySalesReportPage from "./pages/PartyBrandMonthlySalesReportPage";
import ExpenseTransferMonthlyReportPage from "./pages/ExpenseTransferMonthlyReportPage";
import ExpenseSpareWayReportPage from "./pages/ExpenseSpareWayReportPage";
import MonthlyIncentivePage from "./pages/MonthlyIncentivePage";
import MonthlyIncentiveViewPage from "./pages/MonthlyIncentiveViewPage";
import EmployeePortalPage from "./pages/EmployeePortalPage";
import YearlySaleReportPage from "./pages/YearlySaleReportPage";
import SalesDataImportCenterPage from "./pages/SalesDataImportCenterPage";
import SalesDataImportHistoryPage from "./pages/SalesDataImportHistoryPage";
import SalesDataImportRecordsPage from "./pages/SalesDataImportRecordsPage";
import SalesAdvancedReportsPage from "./pages/SalesAdvancedReportsPage";
import SalesVoucherListPage from "./pages/SalesVoucherListPage";
import BranchMonthlySalesPurchasePage from "./pages/BranchMonthlySalesPurchasePage";
import BrandByBranchReportPage from "./pages/BrandByBranchReportPage";
import DiscountSalesReportPage from "./pages/DiscountSalesReportPage";
import ItemsOnSaleDailyReportPage from "./pages/ItemsOnSaleDailyReportPage";
import BranchVoucherReportPage from "./pages/BranchVoucherReportPage";
import GroupByBranchReportPage from "./pages/GroupByBranchReportPage";
import { SalesImportBranchProvider } from "./features/salesDataImport/context/SalesImportBranchContext";
import CreditDuePage from "./pages/credit/CreditDuePage";
import CreditCustomerDetail from "./pages/credit/CreditCustomerDetail";
import CreditCustomerTimelinePage from "./pages/credit/CreditCustomerTimelinePage";
import CreditDueOver15Page from "./pages/credit/CreditDueOver15Page";
import CreditReceivePayment from "./pages/credit/CreditReceivePayment";
import CreditAddSalePage from "./pages/credit/CreditAddSalePage";
import CreditPaymentHistoryPage from "./pages/credit/CreditPaymentHistoryPage";
import CreditEditBillPage from "./pages/credit/CreditEditBillPage";
import CctvLivePage from "./pages/cctv/CctvLivePage";
import CctvCamerasPage from "./pages/cctv/CctvCamerasPage";
import CctvSettingsPage from "./pages/cctv/CctvSettingsPage";

const FirestoreStudioPage = lazy(() => import("./pages/admin/FirestoreStudioPage"));
const BiLayout = lazy(() => import("./features/bi/components/BiLayout"));
const BiDashboardPage = lazy(() => import("./features/bi/pages/BiDashboardPage"));
const BiExecutivePage = lazy(() => import("./features/bi/pages/BiExecutivePage"));
const BiSlowMovingPage = lazy(() => import("./features/bi/pages/BiSlowMovingPage"));
const BiFastMovingPage = lazy(() => import("./features/bi/pages/BiFastMovingPage"));
const BiStockForecastPage = lazy(() => import("./features/bi/pages/BiStockForecastPage"));
const BiDeadStockPage = lazy(() => import("./features/bi/pages/BiDeadStockPage"));
const BiTargetAchievementPage = lazy(() => import("./features/bi/pages/BiTargetAchievementPage"));
const BiBrandGrowthPage = lazy(() => import("./features/bi/pages/BiBrandGrowthPage"));
const BiEmployeePerformancePage = lazy(() => import("./features/bi/pages/BiEmployeePerformancePage"));
const BiProfitAnalysisPage = lazy(() => import("./features/bi/pages/BiProfitAnalysisPage"));
const BiInsightsPage = lazy(() => import("./features/bi/pages/BiInsightsPage"));
const BiBusinessHealthScorePage = lazy(() => import("./features/bi/pages/BiBusinessHealthScorePage"));

function BiLoading() {
  return <LazyRouteFallback />;
}

function RedirectWithQuery({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

export default function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Watch Firebase auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const token = await firebaseUser.getIdTokenResult();
        let fsRole;
        let fsPermissions;
        let fsAllowedBranches;
        try {
          // Fallback source: Firestore user document (helps when claims are missing/stale).
          // Prefer UID-linked row first to avoid duplicate-email/user-name collisions.
          let first;
          if (firebaseUser.uid) {
            const uidSnap = await getDoc(doc(db, "users", firebaseUser.uid));
            if (uidSnap.exists()) first = uidSnap.data();
          }
          if (!first) {
            const email = String(firebaseUser.email || "").trim().toLowerCase();
            if (email) {
              const snap = await getDocs(query(collection(db, "users"), where("email", "==", email)));
              const docs = snap.docs.map((d) => d.data?.() || {});
              first =
                docs.find((d) => String(d?.authUid || "").trim() === String(firebaseUser.uid || "").trim()) ||
                docs[0];
            }
          }
          if (first) {
            fsRole = first.role;
            if (Array.isArray(first.permissions)) fsPermissions = first.permissions;
            if (Array.isArray(first.allowedBranches)) fsAllowedBranches = first.allowedBranches;
          }
        } catch {
          // ignore fallback lookup errors; claims/local defaults still apply
        }

        // Prefer Firestore role/permissions when present (claims can be stale until refreshed).
        const rawRole = fsRole || token.claims.role || "viewer";
        const roleNorm = String(rawRole || "viewer").toLowerCase();
        const role = roleNorm === "administrator" ? "admin" : roleNorm;
        const claimPermissions = Array.isArray(token.claims.permissions) ? token.claims.permissions : undefined;
        const permissions = fsPermissions ?? claimPermissions;
        const claimBranches = Array.isArray(token.claims.allowedBranches) ? token.claims.allowedBranches : undefined;
        const allowedBranches = Array.isArray(fsAllowedBranches) ? fsAllowedBranches : claimBranches;

        const localPayload = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          role,
          ...(permissions ? { permissions } : {}),
          ...(Array.isArray(allowedBranches) ? { allowedBranches } : {}),
        };
        try {
          localStorage.setItem("user", JSON.stringify(localPayload));
        } catch {
          // ignore storage errors
        }
        setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          role,
          permissions,
          ...(Array.isArray(allowedBranches) ? { allowedBranches } : {}),
        });
      } else {
        setUser(null);
        try {
          localStorage.removeItem("user");
        } catch {
          // ignore
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) return <FullPageLoader message="Loading your workspace…" submessage="Verifying authentication" />;

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      <FinancialYearProvider>
      <MaintenanceGate>
        <>
          <AnnouncementBanner />
          <RouteLoadingListener />
          <Routes>
        {/* Public */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route path="/employee-portal" element={<EmployeePortalPage />} />

        {/* Any signed-in user */}
        <Route
          path="/change-password"
          element={
            <ProtectedRoute requirePermission="change_password">
              <ChangePasswordPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/personal"
          element={
            <ProtectedRoute requirePermission={["personal", "change_password"]}>
              <PersonalDataPage />
            </ProtectedRoute>
          }
        />

        {/* Protected */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute requirePermission="dashboard">
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute requirePermission="billing">
              <BillingScreen />
            </ProtectedRoute>
          }
        />
        <Route path="/voucher/:slug" element={<VoucherTransactionPage />} />
        <Route
          path="/credit-due"
          element={
            <ProtectedRoute requirePermission="credit_due">
              <CreditDuePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/credit-due/due-over-15"
          element={
            <ProtectedRoute requirePermission="credit_due">
              <CreditDueOver15Page />
            </ProtectedRoute>
          }
        />
        <Route
          path="/credit-due/customer/:customerId"
          element={
            <ProtectedRoute requirePermission="credit_due">
              <CreditCustomerDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/credit-due/customer/:customerId/timeline"
          element={
            <ProtectedRoute requirePermission="credit_due">
              <CreditCustomerTimelinePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/credit-due/receive"
          element={
            <ProtectedRoute requirePermission="credit_due">
              <CreditReceivePayment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/credit-due/add"
          element={
            <ProtectedRoute requirePermission="credit_due">
              <CreditAddSalePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/credit-due/payments"
          element={
            <ProtectedRoute requirePermission="credit_due">
              <CreditPaymentHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/credit-due/bill/:billId/edit"
          element={
            <ProtectedRoute requirePermission="credit_due">
              <CreditEditBillPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/purchase"
          element={
            <ProtectedRoute requirePermission={voucherPagePermissions(DEFAULT_INWARD_PAGE)}>
              <BillingScreen voucherPageConfig={DEFAULT_INWARD_PAGE} />
            </ProtectedRoute>
          }
        />
        <Route
          path="/party-creation"
          element={
            <ProtectedRoute requirePermission="party_creation">
              <PartyCreation />
            </ProtectedRoute>
          }
        />
        <Route
          path="/transactions/order-reports"
          element={<Navigate to="/reports/order-reports" replace />}
        />
        <Route
          path="/reports/order-reports"
          element={
            <ProtectedRoute requirePermission="order_reports">
              <OrderReportsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/customer-entry/*"
          element={
            <ProtectedRoute requirePermission="customer_entry">
              <CustomerEntryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/*"
          element={
            <ProtectedRoute requirePermission="reports">
              <Reports />
            </ProtectedRoute>
          }
        />

        {/* Admin section */}
        <Route
          path="/admin/projects/*"
          element={
            <ProtectedRoute allowedRoles={["admin"]} requirePermission="admin">
              <Suspense fallback={<LazyRouteFallback />}>
                <FirestoreStudioPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/firestore-manager/*"
          element={
            <ProtectedRoute allowedRoles={["admin"]} requirePermission="admin">
              <FirestoreDataManagerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/*"
          element={
            <ProtectedRoute allowedRoles={["admin"]} requirePermission="admin">
              <AdminPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin-dashboard"
          element={
            <ProtectedRoute allowedRoles={["admin"]} requirePermission="admin">
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Direct protected pages */}
        <Route
          path="/ledger/:partyId"
          element={
            <ProtectedRoute requirePermission="party_creation">
              <PartyLedgerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/party-order-history/:partyId"
          element={
            <ProtectedRoute requirePermission="party_creation">
              <PartyOrderHistoryPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/party-ageing-report"
          element={
            <ProtectedRoute requirePermission="sales_ageing">
              <PartyAgeingReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/monthly-product-report"
          element={
            <ProtectedRoute requirePermission="transactions">
              <MonthlyProductReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-product"
          element={
            <ProtectedRoute requirePermission="masters">
              <AddProductPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stock-report"
          element={
            <ProtectedRoute requirePermission="reports">
              <StockReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/sales-data-import"
          element={
            <ProtectedRoute allowedRoles={["admin", "storekeeper"]}>
              <SalesImportBranchProvider>
                <Outlet />
              </SalesImportBranchProvider>
            </ProtectedRoute>
          }
        >
          <Route index element={<SalesDataImportCenterPage />} />
          <Route path="history" element={<SalesDataImportHistoryPage />} />
          <Route path="records" element={<SalesDataImportRecordsPage />} />
          <Route path="advanced-reports" element={<SalesAdvancedReportsPage />} />
          <Route path="voucher-list" element={<SalesVoucherListPage />} />
          <Route path="branch-monthly" element={<BranchMonthlySalesPurchasePage />} />
          <Route path="brand-by-branch" element={<BrandByBranchReportPage />} />
          <Route path="group-by-branch" element={<GroupByBranchReportPage />} />
          <Route path="discount-sales" element={<DiscountSalesReportPage />} />
          <Route path="items-on-sale" element={<ItemsOnSaleDailyReportPage />} />
          <Route path="branch-voucher-reports" element={<BranchVoucherReportPage />} />
          <Route path="branch-outward-credit" element={<Navigate to="/sales-data-import/branch-voucher-reports" replace />} />
          <Route path="branch-inward-supply" element={<Navigate to="/sales-data-import/branch-voucher-reports" replace />} />
          <Route path="branch-transfer-in" element={<Navigate to="/sales-data-import/branch-voucher-reports" replace />} />
          <Route path="branch-transfer-out" element={<Navigate to="/sales-data-import/branch-voucher-reports" replace />} />
          <Route path="brand-group-branch" element={<Navigate to="/sales-data-import/brand-by-branch" replace />} />
        </Route>
        <Route
          path="/reports/monthly-sale-report"
          element={
            <ProtectedRoute requirePermission="monthly_sale_report">
              <RedirectWithQuery to="/reports/monthly-sale-report-import" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/monthly-sale-report-import"
          element={
            <ProtectedRoute requirePermission="monthly_sale_report">
              <MonthlySaleReportImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/monthly-sale-report-import/generate"
          element={
            <ProtectedRoute requirePermission="monthly_sale_report">
              <MonthlySaleReportImportPrecalcPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/monthly-sale-report/upload"
          element={
            <ProtectedRoute requirePermission="monthly_sale_report">
              <RedirectWithQuery to="/reports/monthly-sale-report-import/generate" />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/expense-upload"
          element={
            <ProtectedRoute requirePermission="monthly_sale_report">
              <ExpenseUploadPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/expense-party-monthly"
          element={
            <ProtectedRoute requirePermission="monthly_sale_report">
              <ExpensePartyMonthlyReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/party-brand-monthly"
          element={
            <ProtectedRoute requirePermission="monthly_sale_report">
              <PartyBrandMonthlySalesReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/expense-transfer-monthly"
          element={
            <ProtectedRoute requirePermission="monthly_sale_report">
              <ExpenseTransferMonthlyReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/expense-spareway-monthly"
          element={
            <ProtectedRoute requirePermission="monthly_sale_report">
              <ExpenseSpareWayReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/yearly-sale-report"
          element={
            <ProtectedRoute requirePermission="yearly_sale_report">
              <YearlySaleReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/monthly-incentive"
          element={
            <ProtectedRoute requirePermission="monthly_incentive">
              <MonthlyIncentivePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/monthly-incentive/view"
          element={
            <ProtectedRoute requirePermission="monthly_incentive">
              <MonthlyIncentiveViewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/movement-analysis"
          element={
            <ProtectedRoute requirePermission={["reports", "movement_analysis", "transactions"]}>
              <MovementAnalysis />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stock-forecast"
          element={
            <ProtectedRoute requirePermission="reports">
              <StockForecastPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-master"
          element={
            <ProtectedRoute requirePermission="masters">
              <GroupMasterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/group-commission-settings"
          element={
            <ProtectedRoute requirePermission="masters">
              <GroupCommissionSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/brand-master"
          element={
            <ProtectedRoute requirePermission="masters">
              <BrandMasterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/unit-master"
          element={
            <ProtectedRoute requirePermission="masters">
              <UnitMasterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gst-master"
          element={
            <ProtectedRoute requirePermission="masters">
              <GstMasterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/hsn-master"
          element={
            <ProtectedRoute requirePermission="masters">
              <HsnMasterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/credit-due-master"
          element={
            <ProtectedRoute requirePermission="masters">
              <CreditDueMasterPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profit-loss"
          element={
            <ProtectedRoute requirePermission="profit_loss">
              <ProfitLossProfessional />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-book"
          element={
            <ProtectedRoute requirePermission="cash_book">
              <CashBook />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bank-book"
          element={
            <ProtectedRoute requirePermission="bank_book">
              <BankBook />
            </ProtectedRoute>
          }
        />
        <Route
          path="/party-Discounts"
          element={
            <ProtectedRoute requirePermission="masters">
              <PartyDiscounts />
            </ProtectedRoute>
          }
        />
        <Route
          path="/party-discount-amounts"
          element={
            <ProtectedRoute requirePermission="masters">
              <PartyDiscountAmountsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/excel-import"
          element={
            <ProtectedRoute requirePermission="tools">
              <ExcelImport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools/pending-order-email"
          element={
            <ProtectedRoute requirePermission="tools">
              <PendingOrderEmailSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/tools/cctv-settings"
          element={
            <ProtectedRoute requirePermission="cctv_admin">
              <CctvSettingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cctv"
          element={
            <ProtectedRoute requirePermission="cctv">
              <CctvLivePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cctv/cameras"
          element={
            <ProtectedRoute requirePermission="cctv_admin">
              <CctvCamerasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/print-bill"
          element={
            <ProtectedRoute requirePermission="billing">
              <BillPrintPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/gstr1"
          element={
            <ProtectedRoute requirePermission="gstr1">
              <Gstr1Page />
            </ProtectedRoute>
          }
        />
        <Route
          path="/purchase-orders"
          element={
            <ProtectedRoute requirePermission="purchase_orders">
              <PurchaseOrdersList />
            </ProtectedRoute>
          }
        />
        <Route
          path="/pending-orders"
          element={
            <ProtectedRoute requirePermission="pending_orders">
              <PendingOrdersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/monthly-report"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollMonthlyLeaveReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/daily-hours"
          element={
            <ProtectedRoute requirePermission="payroll">
              <DailyHoursPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/holidays"
          element={
            <ProtectedRoute requirePermission="payroll">
              <HolidaysPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/daily-late"
          element={
            <ProtectedRoute requirePermission="payroll">
              <DailyLateReportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/yearly-attendance"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollYearlyAttendancePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/employees"
          element={
            <ProtectedRoute requirePermission="payroll">
              <EmployeesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/reports"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollReportsHub />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/reports/employee-directory"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollEmployeeDirectoryReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/reports/tenure"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollTenureMobilityReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/reports/period-attendance"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollPeriodAttendanceReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/reports/ot-summary"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollOtSummaryReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/reports/shift-adherence"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollShiftAdherenceReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/reports/branch-comparison"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollBranchComparisonReport />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/reports/attendance-audit"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollAttendanceAuditNote />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/reports/employee-attendance-card"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollEmployeeAttendanceCard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/payroll/attendance-images"
          element={
            <ProtectedRoute requirePermission="payroll">
              <PayrollAttendanceImagesPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/bi"
          element={
            <ProtectedRoute requirePermission="bi_reports">
              <Suspense fallback={<BiLoading />}>
                <BiLayout />
              </Suspense>
            </ProtectedRoute>
          }
        >
          <Route
            index
            element={
              <Suspense fallback={<BiLoading />}>
                <BiDashboardPage />
              </Suspense>
            }
          />
          <Route path="executive" element={<Suspense fallback={<BiLoading />}><BiExecutivePage /></Suspense>} />
          <Route path="slow-moving" element={<Suspense fallback={<BiLoading />}><BiSlowMovingPage /></Suspense>} />
          <Route path="fast-moving" element={<Suspense fallback={<BiLoading />}><BiFastMovingPage /></Suspense>} />
          <Route path="forecast" element={<Suspense fallback={<BiLoading />}><BiStockForecastPage /></Suspense>} />
          <Route path="dead-stock" element={<Suspense fallback={<BiLoading />}><BiDeadStockPage /></Suspense>} />
          <Route path="target" element={<Suspense fallback={<BiLoading />}><BiTargetAchievementPage /></Suspense>} />
          <Route path="brand-growth" element={<Suspense fallback={<BiLoading />}><BiBrandGrowthPage /></Suspense>} />
          <Route path="employee" element={<Suspense fallback={<BiLoading />}><BiEmployeePerformancePage /></Suspense>} />
          <Route path="profit" element={<Suspense fallback={<BiLoading />}><BiProfitAnalysisPage /></Suspense>} />
          <Route path="insights" element={<Suspense fallback={<BiLoading />}><BiInsightsPage /></Suspense>} />
          <Route path="business-health-score" element={<Suspense fallback={<BiLoading />}><BiBusinessHealthScorePage /></Suspense>} />
        </Route>

        {/* fallback */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
        </>
      </MaintenanceGate>
      </FinancialYearProvider>
    </AuthContext.Provider>
  );
}
